import React from 'react';
import useSourcePackageBuilderStore from '../../../../../stores/useSourcePackageBuilderStore';
import useSourcesExplorerStore from './useSourcesExplorerStore';

/** Map a unified DocumentType to the Sources file viewer's fileType bucket. */
function documentTypeToFileType(documentType) {
    switch (documentType) {
        case 'PDF':
            return 'pdf';
        case 'AUDIO':
            return 'audio';
        case 'VIDEO':
            return 'video';
        case 'IMAGE':
            return 'image';
        default:
            return 'unknown';
    }
}

/** Build the in-app report editor URL for a report id. */
function buildReportUrl(reportId) {
    if (!reportId) {
        return null;
    }
    return `/products/${encodeURIComponent(String(reportId))}/edit`;
}

/**
 * Opens a federated unified-search hit (M6 / M6A) by branching on {@code hit.openTarget.kind}:
 *  - {@code corpus_segment} → corpus segment/document viewer ({@link useSourcePackageBuilderStore}).
 *  - {@code data_item_reference} → Sources file viewer with the sentence anchor for
 *    highlight / seek-on-open.
 *  - {@code report_segment} → open the report read-only in the Source Package overlay
 *    (Products are searchable but not citable as evidence references).
 *  - {@code highlight} → open the bookmarked data item in the Sources file viewer keyed by the
 *    canonical file id, seeding a sentence-anchor from the highlight region so the viewer scrolls
 *    to and marks the highlighted span in context.
 *
 * @param {{reportId: (string|null)}} params
 * @returns {(hit: object) => void}
 */
export default function useOpenSearchHit({reportId} = {}) {
    const openBuilder = useSourcePackageBuilderStore((state) => state.open);
    const setBuilderDocument = useSourcePackageBuilderStore((state) => state.setDocument);
    const isBuilderOpen = useSourcePackageBuilderStore((state) => state.isOpen);

    return React.useCallback((hit) => {
        const target = hit?.openTarget;
        if (!target) {
            return;
        }

        if (target.kind === 'corpus_segment') {
            const documentId = target.docId != null ? String(target.docId) : null;
            if (!documentId) {
                return;
            }
            openBuilder({
                viewKind: 'corpus',
                documentId,
                segmentIndex: target.segmentIndex != null ? target.segmentIndex : null,
                selectedHit: hit,
            });
            return;
        }

        if (target.kind === 'data_item_reference') {
            // Prefer the viewer's own reference; fall back to the canonical file for read-only open.
            const fileId = target.referenceId != null
                ? String(target.referenceId)
                : (target.canonicalFileId != null ? String(target.canonicalFileId) : null);
            if (!fileId) {
                return;
            }

            const filePayload = {
                fileId,
                canonicalFileId: target.canonicalFileId != null ? String(target.canonicalFileId) : null,
                fileName: hit.title || null,
                fileType: documentTypeToFileType(hit.documentType),
                fileSource: hit?.ownership?.inMyFiles ? 'my' : null,
                sentenceHit: hit.sentenceHit || null,
            };

            // My Files now open in the SAME overlay window as Corpus (not a separate modal).
            const openArgs = {viewKind: 'file', filePayload, selectedHit: hit};
            if (isBuilderOpen) {
                setBuilderDocument(openArgs);
            } else {
                openBuilder(openArgs);
            }
            return;
        }

        if (target.kind === 'highlight') {
            // A highlight opens the bookmarked data item in the SAME Sources viewer as My Data.
            // Open by canonical file id and seed a sentence-anchor from the region so the viewer
            // scrolls to and marks the highlighted span in context.
            const fileId = target.canonicalFileId != null ? String(target.canonicalFileId) : null;
            if (!fileId) {
                return;
            }

            const filePayload = {
                fileId,
                canonicalFileId: fileId,
                fileName: hit.title || null,
                fileType: documentTypeToFileType(hit.documentType),
                fileSource: 'my',
                // Seed the viewer anchor from the highlight region so it scrolls to / marks the
                // span. Mirrors the data-item sentenceHit shape; segment-relative offsets come
                // from the highlight region's openTarget.
                sentenceHit: hit.sentenceHit || {
                    segmentId: target.segmentId ?? null,
                    highlightRegionId: target.highlightRegionId ?? null,
                    range: {
                        startOffset: target.startOffset ?? null,
                        endOffset: target.endOffset ?? null,
                        blockIndex: target.blockIndex ?? null,
                    },
                },
            };

            const openArgs = {viewKind: 'file', filePayload, selectedHit: hit};
            if (isBuilderOpen) {
                setBuilderDocument(openArgs);
            } else {
                openBuilder(openArgs);
            }
            return;
        }

        if (target.kind === 'report_segment') {
            const targetReportId = target.reportId != null ? String(target.reportId) : null;

            // Products are searchable and previewable, but never inline-cited as references.
            if (!targetReportId) {
                return;
            }
            const reportPayload = {
                reportId: targetReportId,
                title: hit.title || hit?.ownership?.reportTitle || null,
                // Snippet + query drive excerpt-scroll in the readonly preview (chunkIndex no
                // longer used by the viewer; PM index paths do not line up with the rendered DOM).
                snippet: hit.snippet || null,
                query: useSourcesExplorerStore.getState().debouncedQuery || null,
            };
            const openArgs = {viewKind: 'report', reportPayload, selectedHit: hit};
            if (isBuilderOpen) {
                setBuilderDocument(openArgs);
            } else {
                openBuilder(openArgs);
            }
        }
    }, [openBuilder, setBuilderDocument, isBuilderOpen, reportId]);
}

export {documentTypeToFileType, buildReportUrl};
